import { BaseEdge, EdgeLabelRenderer, type Edge, type EdgeProps } from '@xyflow/react'
import { edgeLabelSize, edgeLabelText, type EdgeLabelPlacement } from '@/frontend/features/Fde/graphEdgeLabelPlacement'
import type { GraphPoint } from '@/frontend/features/Fde/useOntologyGraphLayout'

export interface OntologySchemaEdgeData extends Record<string, unknown> {
  points: GraphPoint[]
  label: string
  cardinality: string | null
  kind: 'link' | 'implements' | 'extends' | 'summary'
  active: boolean
  dimmed: boolean
  showLabel: boolean
  labelPosition: EdgeLabelPlacement | null
}

export type OntologySchemaFlowEdge = Edge<OntologySchemaEdgeData, 'ontologySchema'>

function edgePath(points: GraphPoint[], fallback: GraphPoint[]) {
  const route = points.length > 1 ? points : fallback
  return route.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
}

export default function OntologySchemaEdge({ id, data, markerEnd, sourceX, sourceY, targetX, targetY }: EdgeProps<OntologySchemaFlowEdge>) {
  const points = data?.points || []
  const path = edgePath(points, [{ x: sourceX, y: sourceY }, { x: targetX, y: targetY }])
  const label = data ? edgeLabelText(data.label, data.cardinality) : ''
  const labelSize = edgeLabelSize(label)
  const marker = data?.showLabel ? data.labelPosition : null
  const className = data?.dimmed
    ? 'fde-schema-edge fde-schema-edge-dimmed'
    : data?.active
      ? 'fde-schema-edge fde-schema-edge-active'
      : data?.kind === 'summary'
        ? 'fde-schema-edge fde-schema-edge-summary'
        : data?.kind === 'link'
          ? 'fde-schema-edge'
          : 'fde-schema-edge fde-schema-edge-contract'

  return <>
    <BaseEdge id={id} path={path} markerEnd={markerEnd} interactionWidth={18} className={className} />
    {marker && label && <EdgeLabelRenderer>
      <div
        className="nodrag nopan pointer-events-none absolute z-30 flex items-center justify-center rounded-full border border-border bg-background px-2.5 text-[8px] font-semibold uppercase tracking-wide text-foreground shadow-sm"
        style={{ width: labelSize.width, height: labelSize.height, transform: `translate(-50%, -50%) translate(${marker.x}px, ${marker.y}px)` }}
      >
        <span className="truncate">{label}</span>
      </div>
    </EdgeLabelRenderer>}
  </>
}
